import type {
  UseInfiniteQueryOptions,
  UseQueryOptions,
} from '@tanstack/react-query'
import * as R from 'rambdax'
import type {AsyncReturnType} from 'type-fest'
import type {TizraClient} from '../client'
import type {ApiError} from '../exceptions'
import {logger} from '../log'
import {type Falsy, isObject} from '../types'

const log = logger('tizra/react-query')

export type TizraApiParams<K extends keyof TizraClient> = Parameters<
  TizraClient[K]
>[0]

type TizraUseQueryOptions<
  K extends keyof TizraClient,
  INF extends boolean = false,
> = INF extends true
  ? UseInfiniteQueryOptions<AsyncReturnType<TizraClient[K]>, ApiError>
  : UseQueryOptions<AsyncReturnType<TizraClient[K]>, ApiError>

export type UseApiOptions<
  K extends keyof TizraClient,
  INF extends boolean = false,
> = Partial<TizraUseQueryOptions<K, INF>> & {
  alertOnError?: boolean | ((e: ApiError) => boolean)
  enableDuringHydration?: boolean
}

type FirstArg<K extends keyof TizraClient> =
  | Falsy
  | (TizraApiParams<K> extends undefined ? [true] : never)
  | (TizraApiParams<K> extends string
      ? [TizraApiParams<K>, Parameters<TizraClient[K]>[1]?]
      : TizraApiParams<K>)

type SecondArg<
  K extends keyof TizraClient,
  INF extends boolean,
> = UseApiOptions<K, INF>

export type TizraQueryArgs<K extends keyof TizraClient, INF extends boolean> =
  | (Parameters<TizraClient[K]>[0] extends undefined ? [] : never)
  | [FirstArg<K>, SecondArg<K, INF>?]

type TizraQueryKey<K extends keyof TizraClient> =
  | (TizraApiParams<K> extends undefined ? [K] : never)
  | (TizraApiParams<K> extends string
      ? [K, TizraApiParams<K>, Parameters<TizraClient[K]>[1]?]
      : [K, TizraApiParams<K>])

function disabledQueryFn() {
  throw new Error('unexpected call to disabled queryFn')
}

export function makeQueryParams<
  K extends keyof TizraClient,
  INF extends boolean,
>({
  name,
  api,
  infinite,
  queryArgs,
}: {
  name: K
  api: TizraClient
  infinite: INF
  queryArgs: TizraQueryArgs<K, INF>
}): TizraUseQueryOptions<K, INF> {
  const [firstArg, queryConfig] = queryArgs
  const call = api[name]

  // queryKey is only falsy if it was explicitly passed, not if it was
  // omitted. This is why we have to collect queryArgs before
  // destructuring, so that we can check the length.
  const queryKey: undefined | TizraQueryKey<K> =
    queryArgs.length && !firstArg // undefined, null, false, '', 0
      ? undefined
      : // At this point, undefined definitively means firstArg was
        // omitted, because of queryArgs.length check above.
        firstArg === true || firstArg === undefined
        ? ([name] as TizraQueryKey<K>)
        : // useApi.GET(['foo', {params}])
          Array.isArray(firstArg)
          ? ([name, ...firstArg] as TizraQueryKey<K>)
          : // useApi.query({tizraId: 'foo'})
            ([name, firstArg] as TizraQueryKey<K>)

  // For useInfiniteQuery, start will be appended to the args, for example
  // useInfiniteApi.search(params, start) should integrate start into params.
  const queryFn: any = !queryKey
    ? disabledQueryFn
    : infinite
      ? ({pageParam: start}: {pageParam: number}) =>
          typeof queryKey[1] === 'object' && start
            ? // @ts-ignore too complex
              call({...queryKey[1], start}, ...queryKey.slice(2))
            : // @ts-expect-error spread argument complaints
              call(...queryKey.slice(1))
      : // @ts-expect-error spread argument complaints
        () => call(...queryKey.slice(1))

  const config: Omit<TizraUseQueryOptions<K, INF>, 'queryKey' | 'queryFn'> = {
    // react-query v2 changed from disabling by falsy key.
    // https://github.com/TanStack/query/releases/tag/v2.0.0
    enabled: !!queryKey,

    // react-query v5 changed to required initialPageParam.
    // https://tanstack.com/query/latest/docs/framework/react/guides/migrating-to-v5#infinite-queries-now-need-a-initialpageparam
    ...(infinite && {getNextPageParam, initialPageParam: 0}),

    // queryConfig can be overridden by caller.
    ...(R.omit(['alertOnError', 'enableDuringHydration'], queryConfig) as Omit<
      TizraUseQueryOptions<K, INF>,
      'queryKey' | 'queryFn'
    >),
  }

  return {
    queryKey: queryKey as any,
    queryFn,
    ...config,
  } as TizraUseQueryOptions<K, INF>
}

function getNextPageParam(resp: unknown): number | undefined {
  if (
    isObject(resp) &&
    typeof resp.next === 'string' &&
    typeof resp.size === 'number'
  ) {
    const queryString = resp.next.split('?', 2)[1] || ''
    const nextStart = parseInt(
      new URLSearchParams(queryString).get('start') || '',
      10,
    )
    if (typeof nextStart === 'number' && nextStart < resp.size) {
      return nextStart
    }
  }
}
